import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  TestTube2,
  Menu,
  ChevronLeft, ChevronRight,
  Search, Minus, Plus, Star, GripVertical, EyeOff,
} from 'lucide-react';
import { canAccessRoute } from '../ProtectedRoute';
import { useLabContext } from '../../context/LabContext';
import './Sidebar.css';
import { buildMenuGroups, groupKey, filterMenuByPermissions } from '../../utils/navMenu';
import {
  fetchNavLayout, readCachedLayout, saveNavLayout, applyNavLayout,
  applyHidden, markHidden, layoutFromGroups, moveInArray, NAV_LAYOUT_EVENT,
} from '../../utils/navLayout';

const API_BASE = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? `${window.location.protocol}//${window.location.host}` : `http://${window.location.hostname}:8000`);

const Sidebar = ({ collapsed, onToggle, currentUser }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [permissions, setPermissions] = useState(null);
  const searchInputRef = useRef(null);
  const sidebarRef = useRef(null);

  // Favorites — persisted per-user on the backend so they follow the user
  // across browsers and devices. localStorage is used only as an instant-paint
  // cache to avoid flicker on reload before the API responds.
  const favCacheKey = currentUser?.id ? `sidebarFavorites:u${currentUser.id}` : 'sidebarFavorites';
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(favCacheKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Fetch authoritative favorites on mount / when the user changes. If the
  // legacy browser-only `sidebarFavorites` exists and the server has none,
  // migrate those paths up once so nobody loses their existing favorites.
  useEffect(() => {
    if (!currentUser?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API_BASE}/users/me/favorites`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(async j => {
        if (!j || !Array.isArray(j.paths)) return;
        let paths = j.paths;
        if (paths.length === 0) {
          try {
            const legacy = JSON.parse(localStorage.getItem('sidebarFavorites') || '[]');
            if (Array.isArray(legacy) && legacy.length > 0) {
              await Promise.all(legacy.map(p =>
                fetch(`${API_BASE}/users/me/favorites`, {
                  method: 'POST',
                  headers: { ...headers, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ path: p }),
                }).catch(() => {})
              ));
              paths = legacy;
            }
          } catch {}
        }
        setFavorites(paths);
        try { localStorage.setItem(favCacheKey, JSON.stringify(paths)); } catch {}
      })
      .catch(() => { /* keep localStorage cache on failure */ });
  }, [currentUser?.id, favCacheKey]);

  // The user's own menu order. Same pattern as favorites: paint from the
  // localStorage cache immediately, then take the server's answer as truth.
  const [navLayout, setNavLayout] = useState(() => readCachedLayout(currentUser?.id));

  useEffect(() => {
    if (!currentUser?.id) return;
    let alive = true;
    fetchNavLayout(currentUser.id).then(l => { if (alive) setNavLayout(l); });
    return () => { alive = false; };
  }, [currentUser?.id]);

  // The editor in My Account is on screen at the same time as the sidebar,
  // so a save there has to show up here without a reload.
  useEffect(() => {
    const onChange = (e) => { if (e?.detail) setNavLayout(e.detail); };
    window.addEventListener(NAV_LAYOUT_EVENT, onChange);
    return () => window.removeEventListener(NAV_LAYOUT_EVENT, onChange);
  }, []);

  // Drag-to-reorder state. `kind` separates dragging a whole section from
  // dragging one page, because both live in the same list of <li>s.
  const [drag, setDrag] = useState(null);      // { kind: 'item'|'group', path?, group }
  const [dragOver, setDragOver] = useState(null);  // { kind, path?, group }

  const reorderFavorites = (fromPath, toPath) => {
    if (!fromPath || !toPath || fromPath === toPath) return;
    setFavorites(prev => {
      const fromIdx = prev.indexOf(fromPath);
      const toIdx = prev.indexOf(toPath);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      try { localStorage.setItem(favCacheKey, JSON.stringify(next)); } catch {}
      // Persist the new order to the backend
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API_BASE}/users/me/favorites/reorder`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ paths: next }),
        }).catch(() => { /* best-effort — local state already reflects the move */ });
      }
      return next;
    });
  };

  const toggleFavorite = (path) => {
    const token = localStorage.getItem('token');
    setFavorites(prev => {
      const adding = !prev.includes(path);
      const next = adding ? [...prev, path] : prev.filter(p => p !== path);
      try { localStorage.setItem(favCacheKey, JSON.stringify(next)); } catch {}
      // Fire-and-forget API sync. On failure, roll back the cache so it
      // matches whatever the server has next time this loads.
      if (token) {
        const url = `${API_BASE}/users/me/favorites${adding ? '' : `?path=${encodeURIComponent(path)}`}`;
        const opts = adding
          ? { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ path }) }
          : { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } };
        fetch(url, opts).catch(() => {});
      }
      return next;
    });
  };

  // Click outside the sidebar → collapse (clears any open search)
  useEffect(() => {
    if (collapsed) return;
    const handleDocClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSearchTerm('');
        setSearchFocused(false);
        onToggle();
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [collapsed, onToggle]);
  const userRole = (currentUser?.role || '').toLowerCase();
  const { selectedCompany, selectedCompanyId } = useLabContext();
  const companyLogoUrl = selectedCompany?.logo_url ? (selectedCompany.logo_url.startsWith('http') ? selectedCompany.logo_url : `/api/v1/saas/companies/${selectedCompanyId}/logo-image`) : null;

  // Load permissions on mount
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/page-permissions/my-permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setPermissions(data))
      .catch(() => setPermissions(null));
  }, [currentUser?.role]);

  // The Mobile Collector link is gated on the PROFILE, which /users/me
  // reports. It is not a page permission — /collect is exempt from that
  // matrix on purpose — so there is nothing in `permissions` to consult.
  // Declared HERE, above buildMenuGroups: a useMemo callback runs during
  // render, so a const declared below it is still in the temporal dead zone
  // and the sidebar throws before it paints.
  const isCollector = !!currentUser?.is_mobile_collector;

  // The menu itself lives in utils/navMenu.js so the ordering editor in
  // My Account renders the same list the sidebar does.
  const menuGroups = useMemo(
    () => buildMenuGroups({ userRole, isGlobal: !!currentUser?.is_global, isCollector }),
    [userRole, currentUser?.is_global, isCollector],
  );

  const toggleGroup = (title) => {
    setExpandedGroups(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const isActive = (path) => currentPath === path;

  // Everything this user is allowed to see, in this user's own order.
  //
  // The drag handlers reorder THIS list, never the searched one below — a
  // searched list is a subset, and saving it would persist a menu with the
  // non-matching pages missing.
  const orderableGroups = useMemo(() => {
    let groups = menuGroups;
    // For non-admin roles, filter by dynamic page permissions (DB-driven)
    if (userRole && userRole !== 'admin' && userRole !== 'superadmin') {
      if (permissions) {
        // Use ONLY dynamic permissions — no hardcoded role filtering. Shared
        // with the ordering editor in My Account so the two can't disagree.
        groups = filterMenuByPermissions(groups, { userRole, permissions, isCollector });
      } else {
        // Permissions still loading — use canAccessRoute as fallback
        groups = groups.map(group => ({
          ...group,
          items: group.items.filter(item => (item.requiresCollector
            ? isCollector
            : canAccessRoute(userRole, item.path)))
        })).filter(group => group.items.length > 0);
      }
    }
    // The user's saved order is applied AFTER permissions, so it can only
    // ever rearrange pages they already had.
    return applyNavLayout(groups, navLayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuGroups, permissions, userRole, navLayout]);

  const hiddenPaths = navLayout?.hidden_paths || [];

  const filteredGroups = useMemo(() => {
    let groups = orderableGroups;
    if (searchTerm.trim()) {
      // Search deliberately looks past what the user has hidden. Hiding is
      // for decluttering the menu; a page you can no longer find would be a
      // different, worse feature. Matches are marked so it's clear which
      // ones are not normally on the menu.
      const term = searchTerm.toLowerCase();
      groups = markHidden(groups, hiddenPaths).map(group => ({
        ...group,
        items: group.items.filter(item => item.title.toLowerCase().includes(term))
      })).filter(group => group.items.length > 0);
    } else {
      groups = applyHidden(groups, hiddenPaths);
    }

    // Prepend a FAVORITES group (when any). Honors the user's drag-saved
    // order (from the `favorites` array). Items dropped from the menu (e.g.
    // role lost permission) are silently filtered out. Anything favorited
    // but not yet in the canonical order list (newly added) sticks to the
    // end of the list in insertion order.
    if (favorites.length > 0) {
      // Built from the UNHIDDEN list on purpose: a page you starred stays in
      // FAVORITES even if you hid it from its own section. Starring it is the
      // clearer statement of intent.
      const lookup = new Map();
      orderableGroups.forEach(g => g.items.forEach(it => { lookup.set(it.path, it); }));
      const favItems = favorites.map(p => lookup.get(p)).filter(Boolean);
      if (favItems.length > 0) {
        groups = [{ title: 'FAVORITES', items: favItems, isFavorites: true }, ...groups];
      }
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderableGroups, searchTerm, favorites, navLayout]);

  // ── Persisting a drag ────────────────────────────────────────────────────
  // Always saves the whole picture (every section, every page in it) rather
  // than the one move, so what is stored can't drift from what is on screen.
  const persistOrder = (groups) => {
    // Hiding rides along untouched: a drag must not un-hide anything, and
    // the saved order must keep hidden pages in it so they come back where
    // they were left when un-hidden.
    const layout = layoutFromGroups(groups, hiddenPaths);
    setNavLayout(layout);
    saveNavLayout(currentUser?.id, layout);
  };

  const reorderGroups = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const fromIdx = orderableGroups.findIndex(g => groupKey(g) === fromKey);
    const toIdx = orderableGroups.findIndex(g => groupKey(g) === toKey);
    if (fromIdx < 0 || toIdx < 0) return;
    persistOrder(moveInArray(orderableGroups, fromIdx, toIdx));
  };

  const reorderItem = (gKey, fromPath, toPath) => {
    if (!fromPath || !toPath || fromPath === toPath) return;
    const gIdx = orderableGroups.findIndex(g => groupKey(g) === gKey);
    if (gIdx < 0) return;
    const group = orderableGroups[gIdx];
    const fromIdx = group.items.findIndex(it => it.path === fromPath);
    const toIdx = group.items.findIndex(it => it.path === toPath);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...orderableGroups];
    next[gIdx] = { ...group, items: moveInArray(group.items, fromIdx, toIdx) };
    persistOrder(next);
  };

  const isSearching = searchTerm.trim().length > 0;

  return (
    <aside
      ref={sidebarRef}
      className={`app-sidebar ${collapsed ? 'collapsed' : ''}`}
      /* The menu opens on CLICK, not on hover. It used to expand whenever the
         pointer crossed it and collapse when the pointer left, so crossing the
         left edge on the way to anything else threw the whole page sideways.
         The hamburger is the only thing that opens or closes it now. */
    >
      <div className="app-sidebar-header">
        {/* Always the same three lines in the same place, so it is one target
            that means one thing — not a chevron that changes direction and
            moves when the rail narrows. */}
        <button
          className="app-sidebar-toggle-btn"
          onClick={onToggle}
          aria-label={collapsed ? 'Open menu' : 'Close menu'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Open menu' : 'Close menu'}
        >
          <Menu size={20} />
        </button>
        <div className="app-sidebar-logo-container">
          {!collapsed && (
            <>
              {companyLogoUrl ? (
                <img src={companyLogoUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
              ) : (
                <div className="app-sidebar-logo-icon">
                  <TestTube2 size={20} className="app-sidebar-logo-icon-svg" />
                </div>
              )}
              <div className="app-sidebar-logo-text">
                <h1>{selectedCompany?.legal_name || selectedCompany?.dba_name || 'Dashboard'}</h1>
                <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.65, marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Clinical Lab Operations
                </div>
              </div>
            </>
          )}
          {collapsed && (
            companyLogoUrl ? (
              <img src={companyLogoUrl} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 6 }} />
            ) : (
              <div className="app-sidebar-logo-icon-small">
                <TestTube2 size={20} />
              </div>
            )
          )}
        </div>
      </div>

      {/* Search in Pages */}
      {!collapsed && (
        <div className="sidebar-search-wrapper">
          <div className="sidebar-search-input-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="sidebar-search-input"
              placeholder="Search pages..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={e => { if (e.key === 'Escape') { setSearchTerm(''); e.target.blur(); } }}
            />
            <Search size={16} className="sidebar-search-icon" />
          </div>
        </div>
      )}

      <nav className="app-sidebar-nav">
        <ul className="app-sidebar-nav-list">
          {filteredGroups.map((group, gIdx) => {
            // Groups start collapsed by default. Force-open when:
            //   - the user is searching (so matches are visible), OR
            //   - the group has no title (the top/unnamed group), OR
            //   - the group is the Favorites group, OR
            //   - the user has clicked it open (expandedGroups[title] === true).
            const isExpanded =
              isSearching ||
              group.title === null ||
              group.isFavorites === true ||
              expandedGroups[group.title] === true;

            const gKey = groupKey(group);
            // Reordering is off while searching (the list on screen is a
            // subset then) and while collapsed (nothing to aim at).
            const canReorder = !collapsed && !isSearching;
            // FAVORITES is pinned to the top, so its header doesn't move.
            const groupDraggable = canReorder && !group.isFavorites && !!group.title;
            const groupIsSource = drag?.kind === 'group' && drag.group === gKey;
            const groupIsTarget = groupDraggable && dragOver?.kind === 'group'
              && dragOver.group === gKey && drag?.kind === 'group' && drag.group !== gKey;

            return (
              <React.Fragment key={gKey || gIdx}>
                {group.title && (
                  <li
                    className="sidebar-group-header"
                    onClick={() => toggleGroup(group.title)}
                    draggable={groupDraggable}
                    onDragStart={groupDraggable ? (e) => {
                      setDrag({ kind: 'group', group: gKey });
                      // Firefox won't fire dragover without a payload
                      try { e.dataTransfer.setData('text/plain', gKey); } catch {}
                      e.dataTransfer.effectAllowed = 'move';
                    } : undefined}
                    onDragOver={groupDraggable ? (e) => {
                      if (drag?.kind !== 'group' || drag.group === gKey) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragOver?.group !== gKey || dragOver?.kind !== 'group') {
                        setDragOver({ kind: 'group', group: gKey });
                      }
                    } : undefined}
                    onDragLeave={groupDraggable ? () => {
                      if (dragOver?.kind === 'group' && dragOver.group === gKey) setDragOver(null);
                    } : undefined}
                    onDrop={groupDraggable ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (drag?.kind === 'group') reorderGroups(drag.group, gKey);
                      setDrag(null); setDragOver(null);
                    } : undefined}
                    onDragEnd={groupDraggable ? () => { setDrag(null); setDragOver(null); } : undefined}
                    style={groupDraggable ? {
                      opacity: groupIsSource ? 0.4 : 1,
                      borderTop: groupIsTarget ? '2px solid #3b82f6' : '2px solid transparent',
                      transition: 'opacity 0.15s, border-color 0.15s',
                    } : undefined}
                    title={groupDraggable ? 'Drag to move this section' : undefined}
                  >
                    <span className="sidebar-group-toggle">
                      {isExpanded
                        ? <Minus size={14} className="sidebar-group-toggle-icon" />
                        : <Plus size={14} className="sidebar-group-toggle-icon" />
                      }
                    </span>
                    <span className="sidebar-group-title">{group.title}</span>
                    {groupDraggable && (
                      <span className="sidebar-drag-grip" aria-hidden="true"><GripVertical size={13} /></span>
                    )}
                  </li>
                )}
                {isExpanded && group.items.map((item, index) => {
                  const Icon = item.icon;
                  const hasGroupTitle = !!group.title;
                  const isFav = favorites.includes(item.path);
                  // Every page can be dragged within its own section. In
                  // FAVORITES the move is saved to the favorites order; in a
                  // real section it's saved to the menu layout.
                  const isDraggable = canReorder;
                  const isDragSource = isDraggable && drag?.kind === 'item' && drag.path === item.path;
                  // Only same-section drops are legal — moving a page between
                  // sections would rewrite the menu's structure, not its order.
                  const sameSection = drag?.kind === 'item' && drag.group === gKey;
                  const isDragOver = isDraggable && sameSection
                    && dragOver?.kind === 'item' && dragOver.path === item.path
                    && drag.path !== item.path;
                  return (
                    <li key={`${gKey}-${index}-${item.path}`}
                      className={`app-sidebar-nav-item ${hasGroupTitle ? 'sidebar-child-item' : ''}`}
                      draggable={isDraggable}
                      onDragStart={isDraggable ? (e) => {
                        setDrag({ kind: 'item', path: item.path, group: gKey });
                        // Required for Firefox to actually fire dragover events
                        try { e.dataTransfer.setData('text/plain', item.path); } catch {}
                        e.dataTransfer.effectAllowed = 'move';
                      } : undefined}
                      onDragOver={isDraggable ? (e) => {
                        if (!sameSection || drag.path === item.path) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOver?.path !== item.path || dragOver?.kind !== 'item') {
                          setDragOver({ kind: 'item', path: item.path, group: gKey });
                        }
                      } : undefined}
                      onDragLeave={isDraggable ? () => {
                        if (dragOver?.kind === 'item' && dragOver.path === item.path) setDragOver(null);
                      } : undefined}
                      onDrop={isDraggable ? (e) => {
                        e.preventDefault();
                        if (sameSection && drag.path !== item.path) {
                          if (group.isFavorites) reorderFavorites(drag.path, item.path);
                          else reorderItem(gKey, drag.path, item.path);
                        }
                        setDrag(null); setDragOver(null);
                      } : undefined}
                      onDragEnd={isDraggable ? () => {
                        setDrag(null); setDragOver(null);
                      } : undefined}
                      style={isDraggable ? {
                        // Visual cues during drag
                        opacity: isDragSource ? 0.4 : 1,
                        borderTop: isDragOver ? '2px solid #3b82f6' : '2px solid transparent',
                        transition: 'opacity 0.15s, border-color 0.15s',
                      } : undefined}>
                      <Link
                        to={item.path}
                        className={`app-sidebar-nav-link ${isActive(item.path) ? 'active' : ''}`}
                        // Block click during drag so the link doesn't fire on drop
                        onClick={isDragSource ? (e) => e.preventDefault() : undefined}
                        draggable={false}
                        // Only set while searching: a match the user has hidden
                        // from their menu still shows up, labelled as such.
                        title={item.isHidden ? `${item.title} — hidden from your menu` : undefined}
                        style={item.isHidden ? { opacity: 0.6 } : undefined}
                      >
                        <span className="app-sidebar-nav-icon">
                          {item.isHidden ? <EyeOff size={18} /> : <Icon size={20} />}
                        </span>
                        {/* Rendered in BOTH states. Collapsed it sits under the
                            icon as a small caption — a rail of twenty similar
                            glyphs is unreadable without one. CSS handles the
                            sizing; hiding it here left nothing for CSS to show. */}
                        <span className="app-sidebar-nav-text">{item.title}</span>
                        {!collapsed && (
                          <button
                            type="button"
                            className={`sidebar-fav-btn ${isFav ? 'is-fav' : ''}`}
                            title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.path); }}
                            aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <Star size={14} fill={isFav ? 'currentColor' : 'none'} />
                          </button>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </React.Fragment>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
